/**
 * @fileoverview Shared rich-text editor component for document template authoring.
 * @module ui-admin/components/RichTextEditor
 *
 * Responsibilities: - Provide a full word-processor-grade editing surface for all document bands
 *                     (body text, cover letter, legal disclaimers, etc.).
 *                   - Expose a merge-field picker so authors insert validated data tokens instead
 *                     of typing placeholders by hand.
 * Dependencies:     - TipTap v3 (react, starter-kit, extension-underline, extension-text-style,
 *                     extension-color, extension-font-family, extension-highlight,
 *                     extension-text-align, extension-image, @tiptap/pm).
 *                   - lucide-react for toolbar icons.
 *                   - dompurify for defence-in-depth sanitization on HTML output.
 * Architecture:     - Controlled component: receives `content` (sanitized HTML) and calls
 *                     `onChange(html)` on every editor update. External content changes are
 *                     re-applied only when the prop differs from the last emitted value (prevents
 *                     cursor-jump loops).
 *                   - Merge-field insertion: toolbar dropdown inserts `{{token}}` at the cursor
 *                     via TipTap's `insertContent` command.
 *                   - Editor-agnostic injection: DocumentCanvasBuilder accepts a
 *                     `renderRichTextEditor` render prop — this component fulfils that contract.
 * Notes:            - All TipTap extensions are peer-dependencies of @firesquirrel/ui-admin;
 *                     consuming apps (tenant-web, system-web) must list them in their own
 *                     dependencies.
 *
 * @component RichTextEditor
 * Purpose:     Full-featured TipTap-based rich-text editor with Lucide toolbar, color pickers,
 *              font controls, image upload, and merge-field autocomplete.
 * Props:       content, onChange, mergeFields?, placeholder?
 * Behavior:    Mounts a TipTap editor. Toolbar actions mutate the editor state; the `onUpdate`
 *              handler emits sanitized HTML via `onChange`. Merge-field tokens are inserted at
 *              the current cursor position via a toolbar dropdown.
 * Data Source: none (controlled by parent)
 * Constraints: HTML stored on `Element.html` must already be sanitized; this component applies
 *              a defence-in-depth DOMPurify pass on output (same allowlist as Phase 0).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import DOMPurify from 'dompurify';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, ImageIcon, Quote, RemoveFormatting, ChevronDown, Eye, Pencil,
  Highlighter,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** A single merge-field token available for insertion. */
export interface MergeFieldToken {
  /** Human-readable label shown in the picker dropdown. */
  label: string;
  /** The full token string inserted into the document, e.g. `{{recipientName}}`. */
  value: string;
  /** Optional group label for visual organisation (e.g. "Inspection", "Entity"). */
  group?: string;
}

export interface RichTextEditorProps {
  /** Current HTML content (sanitized). */
  content: string;
  /** Called with sanitized HTML on every change. */
  onChange: (html: string) => void;
  /** Merge-field tokens shown in the toolbar picker. Omit to hide the picker. */
  mergeFields?: MergeFieldToken[];
  /** Editor placeholder text shown when empty. */
  placeholder?: string;
  /** Minimum editor height in pixels. Defaults to 200. */
  minHeight?: number;
}

// ---------------------------------------------------------------------------
// Sanitize helper (defence-in-depth; primary sanitization is on write)
// ---------------------------------------------------------------------------
function sanitize(html: string): string {
  if (typeof window === 'undefined') return html;
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'span', 'strong', 'em', 'u', 's', 'del',
      'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote',
      'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'src', 'alt', 'width', 'height',
      'style', 'class', 'colspan', 'rowspan',
    ],
  });
}

// ---------------------------------------------------------------------------
// Toolbar button
// ---------------------------------------------------------------------------
function TBtn({
  active = false, onClick, title, children, disabled = false,
}: {
  active?: boolean; onClick: () => void; title: string;
  children: React.ReactNode; disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`
        inline-flex items-center justify-center w-7 h-7 rounded
        text-xs font-medium border transition-colors
        ${active
          ? 'bg-gray-900 text-white border-gray-900'
          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'}
        disabled:opacity-40 disabled:cursor-not-allowed
      `}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Toolbar separator
// ---------------------------------------------------------------------------
function Sep() {
  return <div className="w-px h-5 bg-gray-200 mx-0.5 shrink-0" />;
}

// ---------------------------------------------------------------------------
// Merge-field picker dropdown
// ---------------------------------------------------------------------------
function MergeFieldPicker({ editor, mergeFields }: { editor: Editor; mergeFields: MergeFieldToken[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const groups = mergeFields.reduce<Record<string, MergeFieldToken[]>>((acc, f) => {
    const g = f.group ?? 'Fields';
    acc[g] = [...(acc[g] ?? []), f];
    return acc;
  }, {});

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Insert merge field"
        className="inline-flex items-center gap-1 px-2 h-7 rounded-sm text-xs font-medium border border-gray-200 bg-white text-orange-600 hover:bg-orange-50 hover:border-orange-300 transition-colors"
      >
        {'{{ }}'}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] max-h-64 overflow-y-auto py-1">
          {Object.entries(groups).map(([group, fields]) => (
            <div key={group}>
              <div className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">{group}</div>
              {fields.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                  onClick={() => {
                    editor.chain().focus().insertContent(f.value).run();
                    setOpen(false);
                  }}
                >
                  <span className="font-medium">{f.label}</span>
                  <span className="ml-2 text-xs text-gray-400 font-mono">{f.value}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------
function EditorToolbar({ editor, mergeFields }: { editor: Editor; mergeFields?: MergeFieldToken[] }) {
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target?.result as string;
      if (src) editor.chain().focus().setImage({ src, alt: file.name }).run();
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [editor]);

  return (
    <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      {/* Heading / paragraph selector */}
      <select
        value={
          editor.isActive('heading', { level: 1 }) ? '1' :
          editor.isActive('heading', { level: 2 }) ? '2' :
          editor.isActive('heading', { level: 3 }) ? '3' : '0'
        }
        onChange={(e) => {
          const lv = parseInt(e.target.value);
          if (lv === 0) editor.chain().focus().setParagraph().run();
          else editor.chain().focus().toggleHeading({ level: lv as 1 | 2 | 3 }).run();
        }}
        className="px-1.5 py-0.5 border border-gray-200 rounded-sm text-xs bg-white text-gray-700 cursor-pointer"
      >
        <option value="0">Normal</option>
        <option value="1">H1</option>
        <option value="2">H2</option>
        <option value="3">H3</option>
      </select>

      {/* Font family */}
      <select
        value={editor.getAttributes('textStyle').fontFamily ?? ''}
        onChange={(e) => {
          const f = e.target.value;
          if (f) editor.chain().focus().setFontFamily(f).run();
          else editor.chain().focus().unsetFontFamily().run();
        }}
        className="px-1.5 py-0.5 border border-gray-200 rounded-sm text-xs bg-white text-gray-700 cursor-pointer max-w-[90px]"
        title="Font family"
      >
        <option value="">Font</option>
        <option value="Arial, sans-serif">Arial</option>
        <option value="Georgia, serif">Georgia</option>
        <option value="'Times New Roman', serif">Times New Roman</option>
        <option value="'Courier New', monospace">Courier New</option>
        <option value="Verdana, sans-serif">Verdana</option>
        <option value="Trebuchet MS, sans-serif">Trebuchet</option>
        <option value="Impact, sans-serif">Impact</option>
      </select>

      <Sep />

      {/* Bold / Italic / Underline / Strikethrough */}
      <TBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (Ctrl+B)">
        <Bold className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (Ctrl+I)">
        <Italic className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline (Ctrl+U)">
        <UnderlineIcon className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
        <Strikethrough className="h-3.5 w-3.5" />
      </TBtn>

      <Sep />

      {/* Text color */}
      <label title="Text colour" className="inline-flex items-center gap-0.5 px-1.5 h-7 border border-gray-200 rounded-sm bg-white hover:bg-gray-100 cursor-pointer transition-colors">
        <span className="text-xs font-bold text-gray-700" style={{ color: editor.getAttributes('textStyle').color ?? 'inherit' }}>A</span>
        <input
          type="color"
          value={editor.getAttributes('textStyle').color ?? '#000000'}
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          className="w-3.5 h-3.5 p-0 border-0 bg-transparent cursor-pointer rounded-sm"
        />
      </label>

      {/* Highlight */}
      <label title="Highlight colour" className="inline-flex items-center gap-0.5 px-1.5 h-7 border border-gray-200 rounded-sm bg-white hover:bg-gray-100 cursor-pointer transition-colors">
        <Highlighter className="h-3.5 w-3.5 text-gray-600" />
        <input
          type="color"
          value={editor.getAttributes('highlight').color ?? '#fef08a'}
          onChange={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
          className="w-3.5 h-3.5 p-0 border-0 bg-transparent cursor-pointer rounded-sm"
        />
      </label>

      <Sep />

      {/* Lists */}
      <TBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list">
        <List className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">
        <ListOrdered className="h-3.5 w-3.5" />
      </TBtn>

      <Sep />

      {/* Alignment */}
      <TBtn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align left">
        <AlignLeft className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align centre">
        <AlignCenter className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align right">
        <AlignRight className="h-3.5 w-3.5" />
      </TBtn>
      <TBtn active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justify">
        <AlignJustify className="h-3.5 w-3.5" />
      </TBtn>

      <Sep />

      {/* Link */}
      <TBtn
        active={editor.isActive('link')}
        onClick={() => {
          const current = editor.getAttributes('link').href ?? '';
          const url = window.prompt('Link URL:', current);
          if (url === null) return;
          if (url === '') editor.chain().focus().unsetLink().run();
          else editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
        }}
        title="Insert / edit link"
      >
        <LinkIcon className="h-3.5 w-3.5" />
      </TBtn>

      {/* Image upload */}
      <TBtn active={false} onClick={() => imageInputRef.current?.click()} title="Insert image">
        <ImageIcon className="h-3.5 w-3.5" />
      </TBtn>
      <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />

      {/* Blockquote */}
      <TBtn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote">
        <Quote className="h-3.5 w-3.5" />
      </TBtn>

      {/* Clear formatting */}
      <TBtn active={false} onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear formatting">
        <RemoveFormatting className="h-3.5 w-3.5" />
      </TBtn>

      {/* Merge field picker */}
      {mergeFields && mergeFields.length > 0 && (
        <>
          <Sep />
          <MergeFieldPicker editor={editor} mergeFields={mergeFields} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @component RichTextEditor
 */
export default function RichTextEditor({
  content,
  onChange,
  mergeFields,
  placeholder = 'Start typing…',
  minHeight = 200,
}: RichTextEditorProps) {
  const [showPreview, setShowPreview] = useState(false);
  const lastEmitted = useRef(content);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ underline: false }),
      Underline,
      TextStyle,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({ inline: true, allowBase64: true }),
    ],
    content,
    editorProps: {
      attributes: {
        class: 'focus:outline-hidden',
        style: `min-height:${minHeight}px; padding: 12px 16px; font-size: 14px; line-height: 1.6;`,
        'data-placeholder': placeholder,
      },
    },
    onUpdate({ editor: e }) {
      const html = sanitize(e.getHTML());
      lastEmitted.current = html;
      onChange(html);
    },
  });

  // Sync external content changes (e.g. undo from parent, template load)
  useEffect(() => {
    if (editor && content !== lastEmitted.current) {
      lastEmitted.current = content;
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-xs">
      {/* Top bar: toolbar + preview toggle */}
      <div className="flex items-start justify-between bg-gray-50 border-b border-gray-200">
        {!showPreview && editor ? (
          <EditorToolbar editor={editor} mergeFields={mergeFields} />
        ) : (
          <div className="flex-1 px-2 py-1.5" />
        )}
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className={`
            shrink-0 inline-flex items-center gap-1 px-3 py-1.5 m-1 rounded text-xs font-medium transition-colors
            ${showPreview
              ? 'bg-gray-900 text-white'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-200'}
          `}
        >
          {showPreview
            ? <><Pencil className="h-3 w-3" /> Edit</>
            : <><Eye className="h-3 w-3" /> Preview</>}
        </button>
      </div>

      {/* Editor / preview */}
      {showPreview ? (
        <div
          className="prose max-w-none px-4 py-3"
          style={{ minHeight }}
          dangerouslySetInnerHTML={{ __html: sanitize(content) }}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  );
}
